import { colors } from '@ardoq/design-tokens';
import { HTMLProps, useState } from 'react';
import styled from 'styled-components';
import { IconName, IconSize } from './types';
const ImageIconStyle = styled.img<{ $iconSize: IconSize }>`
  object-fit: contain;
  width: ${({ $iconSize }) => `${$iconSize}px`};
  height: ${({ $iconSize }) => `${$iconSize}px`};
`;

// Cache of URLs that have failed to load. Prevents repeated requests when
// components remount during scroll (e.g. in virtualized lists like the inventory).
//
// Note: The img onError event doesn't expose HTTP status, so all failures are
// cached (404s, network errors, 5xx, etc). This assumes failed URLs stay failed
// for the session - appropriate for deleted attachments, but transient errors
// would require a page refresh to retry.
const brokenImageUrls = new Set<string>();

type ImageIconProps = { iconSize: IconSize } & Pick<
  HTMLProps<HTMLImageElement>,
  'src' | 'className' | 'data' | 'alt'
>;

type BrokenImageIconProps = {
  iconSize: IconSize;
};

// A bit raw, low level implementation, but I cant use `<Icon />` here, because
// Icon use ImageIcon so we have a cycle dependency issue
const BrokenImageIcon = ({ iconSize }: BrokenImageIconProps) => (
  <svg
    data-tooltip-text="Image cannot be displayed"
    className="ardoq-icon"
    style={{
      width: iconSize,
      height: iconSize,
      minWidth: iconSize,
      color: colors.red700,
    }}
  >
    <use xlinkHref={`#${IconName.BROKEN_IMAGE}`}></use>
  </svg>
);

// Do not expose the ImageIcon from the package, it might not work as expected
// eg. the parent `<Icon />` component is applying `ICON_SELECTOR` class name
// which this low level component doesn't do
export const ImageIcon = ({ iconSize, src, ...rest }: ImageIconProps) => {
  const [error, setError] = useState(() =>
    Boolean(src && brokenImageUrls.has(src))
  );

  const onError = () => {
    if (src) brokenImageUrls.add(src);
    setError(true);
  };

  if (error) return <BrokenImageIcon iconSize={iconSize} {...rest} />;
  return (
    <ImageIconStyle
      src={src}
      onError={onError}
      $iconSize={iconSize}
      {...rest}
    />
  );
};
